import 'vue3-carousel/dist/carousel.css'
import './styles/old-global.styl'
import './styles/global.styl'
import './styles/tables.styl'
import {createApp, nextTick} from 'vue'
import * as directives from './directives'
import PrimeVue from 'primevue/config'
import App from './app'
import store from 'stores/global'
import router from 'router'
import {VueRangy, VueToasts, VueResponsive} from 'plugins'
import {Amplify} from 'aws-amplify'
import {AuthError} from 'aws-amplify/auth'
import AmplifyVue from '@aws-amplify/ui-vue'
import * as Sentry from '@sentry/vue'
import {BadGateway, Cancelled, Maintenance, Unauthorized} from 'utils/errors'
import {Mounted, Throttle, EventBus, VReadMixin, Debounce} from './mixins'


import * as $utils from 'utils';
if (store.max_style) {
  document.title = 'Max'
  document.querySelectorAll('[data-brand]').forEach(meta => meta.content = 'Max')
  document.querySelectorAll('[data-size]').forEach(link => {
    const size = link.dataset.size
    link.href = `/assets/app-icons/max-appicon-${size}.png`
  })
}

if (!LOCAL_FRONT) {
  //
  // In non-local mode we force HTTPS
  //
  if (window.location.protocol !== 'https:') {
    location.replace(`https:${location.href.substring(location.protocol.length)}`)
  }
}

//
// Check UI version and cleanup if changed
//
const VERSION_KEY = 'version'
const REALMS_KEY = 'realms'
const VERSION = '0.0.3'

if (localStorage.getItem(VERSION_KEY) !== VERSION) {
  console.log('UI version changed: clearing local storage')
  localStorage.clear()
  localStorage.setItem(VERSION_KEY, VERSION)
}

//
// Configure authentication
// TODO: This can take time. Need to show loading indicator but nothing exist at this moment :(
//       Unsure how to fix this. Amplify must be initialized before router
//
nextTick(async () => {
  const cognito = {
    userPoolId: COGNITO_POOL,
    userPoolClientId: COGNITO_CLIENT,
    passwordFormat: {
      minLength: 12,
      requireLowercase: true,
      requireUppercase: true,
      requireNumbers: true,
      requireSpecialCharacters: true,
    }
  }

  const realmProviders = JSON.parse(PROVIDERS)
  const realms = realmProviders.providers
  console.log(`Auth realms ${realms}`)

  // if realms changed need to clear local amplify state or getUser might never return
  const oldRealms = localStorage.getItem(REALMS_KEY)
  if (oldRealms && oldRealms !== realms.toString()) {
    const to_remove = Object.keys(localStorage).filter(key => key.startsWith('Cognito'))
    to_remove.forEach(key => localStorage.removeItem(key))
  }

  if (realms.length) {
    // idp_initiated=false should go first. First oauth url is used by amplify in SP-initiated flow
    const urls = [`${window.location.origin}/login?idp_initiated=false`]

    for (const realm of realms) {
      urls.push(`${window.location.origin}/login?idp_initiated=true&idp=${encodeURIComponent(realm)}`)
    }

    cognito['loginWith'] = {
      oauth: {
        redirectSignIn: urls,
        redirectSignOut: [`${window.location.origin}/login`],
        responseType: 'code',
        scopes: ['aws.cognito.signin.user.admin', 'openid', 'email', 'profile'],
        domain: 'authorize.dev.talking.buzz',
      }
    }
  }

  // Monkey-patch fetch to force credentials for token calls
  const originalFetch = window.fetch
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url
    if (url && url.includes('/oauth2/token')) {
      args[1] = args[1] || {}
      args[1].credentials = 'include'
    }
    return originalFetch.apply(this, args)
  }

  localStorage.setItem(REALMS_KEY, realms)
  Amplify.configure({
    Auth: {
      Cognito: cognito
    }
  })

  //
  // Setup Vue
  //
  const vueApp = createApp(App)


  vueApp.mixin(VReadMixin)
  vueApp.mixin(Debounce)
  vueApp.mixin(Throttle)
  vueApp.mixin(EventBus)
  vueApp.mixin(Mounted)
  Object.keys(directives).forEach(key => vueApp.directive(key, directives[key]))

  vueApp.config.errorHandler = (err, instance, info) => {
    store.setError(err)
  }

  window.addEventListener('error', ev => {
    ev.preventDefault()
    store.setError(ev.error)
  })

  window.addEventListener('unhandledrejection', ev => {
    ev.preventDefault()
    store.setError(ev.reason)
  })


  //TODO: It's quick fix for ResizeObserver loop
  // need to investigate it
  const _ResizeObserver = window.ResizeObserver
  window.ResizeObserver = class ResizeObserver extends _ResizeObserver{
    constructor(callback) {
      callback = $utils.timers.debounce(callback, 16)
      super(callback)
    }
  }

  vueApp.config.globalProperties.$store = store
  vueApp.config.globalProperties.$global = store
  vueApp.config.globalProperties.$state = store.state
  vueApp.config.globalProperties.$utils = $utils
  vueApp.config.unwrapInjectedRef = true

  if (SENTRY_DSN !== 'false') {
    Sentry.init({
      app: vueApp,
      dsn: SENTRY_DSN,
      beforeSend(event, hint) {
        const error = hint.originalException
        if (error instanceof AuthError){
          if (error.name === 'UserAlreadyAuthenticatedException') {
            return null
          }
        }
        if (error instanceof Maintenance || error instanceof Unauthorized || error instanceof BadGateway || error instanceof Cancelled) {
          return null
        }
        return event
      },
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
      ],
      tracesSampleRate: 0.2,
      profilesSampleRate: 0.05
    })
  }

  const responsive = {
    mobile:  '(max-width: 767px)',
    tablet: '(max-width: 1249px)'
  }
  const is_mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  if (is_mobile) {
    responsive.tablet = '(max-width: 99999px)'
  }
  else {
    responsive.desktop = '(min-width: 1250px)'
  }


  vueApp
    .use(PrimeVue, {unstyled: true})
    .use(VueToasts)
    .use(VueResponsive, responsive)
    .use(VueRangy, {init: true})
    .use(AmplifyVue)
    .use(router)

  router.$responsive = vueApp.config.globalProperties.$responsive
  vueApp.mount('body')

  // As per browser docs add listener to 'unload' event
  // disables back/forward button cache. This is from security reqs
  // Works only on desktop!
  window.addEventListener('unload', e => {})

  // b/f cache handling for mobile browsers
  // Since 'unload' event doesn't work we are
  // catching back/forward navigation and force full reload
  window.addEventListener('pageshow', e => {
    if (e.persisted) {
      document.documentElement.style.display = 'none'
      location.reload()
    }
  })
})
